import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '@/api/apiCall';
import { toast } from 'sonner';
import carpark1 from '@/assets/img/car-park-1.png';
import { useApiQueue } from '@/hooks/useApiQueue';
import { AssetsPanel } from '../../components/shared/AssetsPanel';
import { API_CALL_TIME } from '@/constants/constant';
import LoadingComponent from '@/components/common/LoadingComponent';

// ─── Fallback Data ────────────────────────────────────────────────────────────
const FALLBACK_PROFILE = {
  table_key: 'dammam_airport_profile',
  title: 'Dammam Airport Parking Profile',
  data: {
    car_park_bays:     { label: 'Total Off Street Car Park Bays', value: '7861' },
    contract_term:     { label: 'Contract Term', value: '25 years' },
    contract_period:   { start_date: '12.05.2014', end_date: '11.05.2039' },
    operational_hours: { label: 'Operational Hours', value: '24 Hours Per Day' },
    vat_note:          { label: 'Parking & Membership Charges', value: 'Excluding VAT 15%' },
  },
};

const FALLBACK_MEMBERSHIP = {
  table_key: 'dammam_airport_membership_charges',
  title: 'Membership Details',
  data: {
    columns: [
      { column_key: 'membership_details',            label: '#Membership Details',              type: 'text' },
      { column_key: 'payment',                       label: '#Payment',                         type: 'text' },
      { column_key: 'allowed_continuous_parking_hrs',label: 'Allowed Continuous Parking Hrs',   type: 'text' },
      { column_key: 'membership_period',             label: 'Membership Period',                type: 'text' },
      { column_key: 'excess_hours',                  label: 'Excess Hours',                     type: 'text' },
    ],
    rows: [
      { membership_details: 'Government and Police vehicles',                        payment: 'FREE',    allowed_continuous_parking_hrs: 'Continuous Parking',         membership_period: '1 Year',    excess_hours: 'N/A'         },
      { membership_details: 'Government Employees, Police, DACO- Personal Vehicles', payment: 'FREE',    allowed_continuous_parking_hrs: 'Continuous 14 Days Allowed', membership_period: 'One Month', excess_hours: 'Normal Rate' },
      { membership_details: 'SV-Saudi Arabian Airlines',                             payment: '150 SR',  allowed_continuous_parking_hrs: 'Continuous 24 Hrs',          membership_period: 'One Month', excess_hours: 'Normal Rate' },
      { membership_details: 'SGS-Saudi Ground Services',                             payment: '150 SR',  allowed_continuous_parking_hrs: 'Continuous 18 Hrs',          membership_period: 'One Month', excess_hours: 'Normal Rate' },
      { membership_details: 'Customers helpdesk-DACO',                               payment: 'FREE',    allowed_continuous_parking_hrs: 'Continuous 12 Hrs',          membership_period: 'One Month', excess_hours: 'Normal Rate' },
      { membership_details: 'VIP Terminal Employee',                                 payment: 'FREE',    allowed_continuous_parking_hrs: 'Continuous 12 Hrs',          membership_period: 'One Month', excess_hours: 'Normal Rate' },
    ],
  },
};

const FALLBACK_CASUAL = {
  table_key: 'dammam_airport_casual_parking_rates',
  title: 'Casual Parking Price List',
  data: {
    columns: ['Casual Parking Price List', '#Payment'],
    rows: [
      ['Per Hours (If Pay by at Exit Cashier)',     '4 SR'  ],
      ['Per Hours (If Pay by APM & APP payment)',   '3 SR'  ],
      ['Towing Charges',                            '200 SR'],
      ['RFID Additional (First one free)',          '100 SR'],
      ['Storage Amount after 24 hours per day',    '50 SR' ],
      ['Long Term Parking Area 24 hours per day',  '24 SR' ],
    ],
  },
};

const FALLBACK_BAYS = {
  table_key: 'dammam_airport_parking_bays_breakdown',
  title: 'DAP-Dammam Airport Parking Bays Breakdown',
  data: {
    columns: ['DAP-DAMMAM AIRPORT PARKING', 'Parking Bays 7861'],
    rows: [
      ['EMPLOYEES PARKING', '950'  ],
      ['NORTH',             '1,774'],
      ['SOUTH POUND',       '37'   ],
      ['SOUTH',             '2,050'],
      ['VIP',               '50'   ],
      ['LONG TERM PARKING', '3,000'],
      // ['BUS PARKING',       '20'],
      // ['ECONOMY PARKING',   '1,580'],
    ],
  },
};

const FALLBACK_ASSETS = {
  table_key: 'dammam_airport_assets_deployed',
  title: 'Dammam Airport',
  data: {
    groups: [
      { group_name: 'Ticket Dispenser',     group_total_qty: '15', items: [{ item_name: 'DIP G100', qty: '15' }, { item_name: 'G100 64 Bit', qty: '-' }, { item_name: 'G100 32 Bit', qty: '-' }] },
      { group_name: 'Ticket Verifier',      group_total_qty: '18', items: [{ item_name: 'EV DIP G100', qty: '18' }, { item_name: 'EV G100 64 Bit', qty: '-' }, { item_name: 'EV G100 32 Bit', qty: '-' }] },
      { group_name: "APM's",               group_total_qty: '9',  items: [{ item_name: 'APM ETS-70', qty: '3' }, { item_name: 'APM ETS-100', qty: '6' }] },
      { group_name: 'PIN Pad',              group_total_qty: '26', items: [{ item_name: 'IUP250', qty: '26' }] },
      { group_name: 'Card Reader',          group_total_qty: '1',  items: [{ item_name: 'IUR250', qty: '1' }] },
      { group_name: 'B-Contactless',        group_total_qty: '26', items: [{ item_name: 'IUC150', qty: '26' }] },
      { group_name: 'RFID Reader',          group_total_qty: '35', items: [] },
      { group_name: 'IP Barrier',           group_total_qty: '43', items: [] },
      { group_name: 'ANPR Camera',          group_total_qty: '34', items: [{ item_name: 'ARH', qty: '34' }, { item_name: 'Lumenera', qty: '-' }] },
      { group_name: 'Sliding Gate',         group_total_qty: '-',  items: [{ item_name: 'RIGHT', qty: '-' }, { item_name: 'LEFT', qty: '-' }] },
      { group_name: "PND's",               group_total_qty: '-',  items: [{ item_name: 'TPAL BNA-Large', qty: '-' }, { item_name: 'T-PAL TX', qty: '-' }, { item_name: 'T-PAL V2', qty: '-' }] },
      { group_name: 'Pound Cashier Device', group_total_qty: '-',  items: [{ item_name: 'CN-50', qty: '-' }] },
      { group_name: 'Driver Device',        group_total_qty: '6',  items: [{ item_name: 'CN-51', qty: '6' }] },
      { group_name: 'Enf Device',           group_total_qty: '-',  items: [{ item_name: 'Oukitel WP2', qty: '-' }, { item_name: 'Doogee V10', qty: '-' }] },
      { group_name: 'Validation',           group_total_qty: '-',  items: [{ item_name: 'Lenova Tablet', qty: '-' }] },
      { group_name: 'Printer',              group_total_qty: '4',  items: [{ item_name: 'PR-3', qty: '4' }, { item_name: 'SEWOO', qty: '-' }, { item_name: 'BIXOLON', qty: '-' }] },
      { group_name: 'Tow Truck',            group_total_qty: '3',  items: [] },
      { group_name: 'Clamp Vehicle',        group_total_qty: '-',  items: [] },
      { group_name: 'Support Vehicle',      group_total_qty: '3',  items: [] },
    ],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const groupsToFlat = (groups = []) => {
  const result = [];
  groups.forEach(g => {
    result.push({ name: g.group_name || '', isCategory: true, subCount: '', totalCount: String(g.group_total_qty ?? '-') });
    (g.items || []).forEach(item => {
      result.push({ name: item.item_name || '', isCategory: false, subCount: String(item.qty ?? '-'), totalCount: '' });
    });
  });
  return result;
};

const flatToGroups = (flat) => {
  const groups = [];
  let cur = null;
  flat.forEach(a => {
    if (a.isCategory) {
      if (cur) groups.push(cur);
      cur = { group_name: a.name, group_total_qty: a.totalCount || '', items: [] };
    } else if (cur) {
      cur.items.push({ item_name: a.name, qty: a.subCount || '' });
    }
  });
  if (cur) groups.push(cur);
  return groups;
};

// ─────────────────────────────────────────────────────────────────────────────
const DammamAirport_2 = ({ year = '', monthNumber = '', month = '', contract = '' }) => {
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  // 🔥 State for second API (parking bays)
  const [parkingBaysData, setParkingBaysData] = useState(null);

  // ── State ─────────────────────────────────────────────────────────────────
  const [profileTable,    _setProfileTable]    = useState(FALLBACK_PROFILE);
  const [membershipTable, _setMembershipTable] = useState(FALLBACK_MEMBERSHIP);
  const [casualTable,     _setCasualTable]     = useState(FALLBACK_CASUAL);
  const [baysTable,       _setBaysTable]       = useState(FALLBACK_BAYS);
  const [assetsTable,     _setAssetsTable]     = useState(FALLBACK_ASSETS);

  // ── FIX: Refs to always hold latest state (prevents stale closure in saveToAPI) ──
  const profileRef    = useRef(FALLBACK_PROFILE);
  const membershipRef = useRef(FALLBACK_MEMBERSHIP);
  const casualRef     = useRef(FALLBACK_CASUAL);
  const baysRef       = useRef(FALLBACK_BAYS);
  const assetsRef     = useRef(FALLBACK_ASSETS);

  // ── Wrapped setters that keep refs in sync ────────────────────────────────
  const setProfileTable = (valOrFn) => {
    _setProfileTable(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      profileRef.current = next;
      return next;
    });
  };

  const setMembershipTable = (valOrFn) => {
    _setMembershipTable(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      membershipRef.current = next;
      return next;
    });
  };

  const setCasualTable = (valOrFn) => {
    _setCasualTable(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      casualRef.current = next;
      return next;
    });
  };

  const setBaysTable = (valOrFn) => {
    _setBaysTable(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      baysRef.current = next;
      return next;
    });
  };

  const setAssetsTable = (valOrFn) => {
    _setAssetsTable(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      assetsRef.current = next;
      return next;
    });
  };

  const { enqueue } = useApiQueue();

  // 🔥 Effective total bays - second API se aayega, nahi toh fallback
  const effectiveTotalBays = parkingBaysData?.total_bays ?? profileTable.data.car_park_bays?.value ?? '0';

  // ── Fetch BOTH APIs ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchAllData();
  }, [year, month, monthNumber, contract]);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      // 🔥 Second API params - 'type' not 'value'
      const secondApiParams = {
        contract: "0030003",
        year: year,
        month: month,     // using 'month' (string like "Oct")
        type: "airport"
      };
      
      const [firstApiResponse, secondApiResponse] = await Promise.all([
        enqueue(() => apiCall('GET', '/boardpack/get-tables-data', { year, contract, month: monthNumber })),
        enqueue(() => apiCall('GET', '/boardpack/parking/bays', secondApiParams))
      ]);

      // Process first API response
      if (firstApiResponse?.success) {
        const data = firstApiResponse?.data || [];
        const find = (key, fb) => data.find(t => t.table_key === key) || fb;

        setProfileTable(find('dammam_airport_profile',               FALLBACK_PROFILE));
        setMembershipTable(find('dammam_airport_membership_charges', FALLBACK_MEMBERSHIP));
        setCasualTable(find('dammam_airport_casual_parking_rates',   FALLBACK_CASUAL));
        setBaysTable(find('dammam_airport_parking_bays_breakdown',   FALLBACK_BAYS));
        setAssetsTable(find('dammam_airport_assets_deployed',        FALLBACK_ASSETS));
      }

      // 🔥 Process second API response for parking bays
      if (secondApiResponse?.success && secondApiResponse?.data) {
        setParkingBaysData(secondApiResponse.data);
      } else {
        console.warn("Second API failed:", secondApiResponse?.message);
        setParkingBaysData(null);
      }
      
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const autoSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  // FIX: Now reads from refs → always gets the LATEST state, not stale closure
  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const response = await apiCall('POST', '/boardpack/add-tables-data', {
        contract, year, month: monthNumber, created_by: 1,
        tables: [
          { table_key: profileRef.current.table_key,    title: profileRef.current.title,    data: profileRef.current.data    },
          { table_key: membershipRef.current.table_key, title: membershipRef.current.title, data: membershipRef.current.data },
          { table_key: casualRef.current.table_key,     title: casualRef.current.title,     data: casualRef.current.data     },
          { table_key: baysRef.current.table_key,       title: baysRef.current.title,       data: baysRef.current.data       },
          { table_key: assetsRef.current.table_key,     title: assetsRef.current.title,     data: assetsRef.current.data     },
        ],
      });
      if (!response?.success) throw new Error(response?.message);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const patchProfile = (field, subField, value) => {
    setProfileTable(prev => ({
      ...prev,
      data: { ...prev.data, [field]: subField ? { ...prev.data[field], [subField]: value } : value },
    }));
    autoSave();
  };

  // 🔥 Updated: Total bays ab READ-ONLY hai, isliye handleTotalBays sirf bays table ka column update karega
  const handleTotalBays = (value) => {
    // Ab total bays edit nahi hoga - second API se aata hai
    // Sirf bays table ka column label update karo agar zaroori ho
    setBaysTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        columns: [prev.data.columns[0], `Parking Bays ${effectiveTotalBays}`],
      },
    }));
    autoSave();
  };

  const handleMembershipRow = (rowIdx, field, value) => {
    setMembershipTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((row, i) => i === rowIdx ? { ...row, [field]: value } : row),
      },
    }));
    autoSave();
  };

  const handleCasualRow = (rowIdx, colIdx, value) => {
    setCasualTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((row, i) =>
          i === rowIdx ? row.map((cell, j) => j === colIdx ? value : cell) : row
        ),
      },
    }));
    autoSave();
  };

  const handleBayRow = (rowIdx, colIdx, value) => {
    setBaysTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((row, i) =>
          i === rowIdx ? row.map((cell, j) => j === colIdx ? value : cell) : row
        ),
      },
    }));
    autoSave();
  };

  const handleAssetSubCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current.data.groups).map((a, i) => i === flatIdx ? { ...a, subCount: value } : a);
    setAssetsTable(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  const handleAssetTotalCount = (flatIdx, value) => {
    const flat = groupsToFlat(assetsRef.current.data.groups).map((a, i) => i === flatIdx ? { ...a, totalCount: value } : a);
    setAssetsTable(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(flat) } }));
    autoSave();
  };

  // ── Accessors ─────────────────────────────────────────────────────────────
  const pd  = profileTable.data;
  const md  = membershipTable.data;
  const cd  = casualTable.data;
  const bd  = baysTable.data;

  const memColumns  = md.columns || [];
  const memRows     = md.rows    || [];
  const casualRows  = cd.rows    || [];
  const baysRows    = bd.rows    || [];

  const flatAssets  = groupsToFlat(assetsTable.data.groups);

  const thCls = '!border !border-black px-2 py-1 text-[9px] font-bold text-center bg-gray-300';
  const tdCls = '!border !border-black px-2 py-1 text-[9px]';

  if (isLoading) {
    return <LoadingComponent/>
  }

  return (
    <div className="min-h-screen p-8 bg-white">

      {/* Title */}
      <div className="text-center mb-4">
        <h1 className="text-xl font-bold">{profileTable.title}</h1>
      </div>

      <div className="flex flex-row gap-0 !border-2 !border-black p-3">

        {/* ══════════════════ LEFT COLUMN ══════════════════ */}
        <div className="flex-1 !border-black pr-4">

          {/* Top Info Block */}
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 flex flex-col items-center flex-shrink-0">
              <img src={carpark1} alt="Car Park" className="w-10 h-10 object-contain" />
            </div>
            <div className="text-[11px] leading-relaxed">

              {/* 🔥 READ-ONLY Total Bays - Second API se aayega */}
              <div className="text-[13px] mb-1">
                Total Off Street Car Park Bays{' '}
                <input 
                  type="text" 
                  value={effectiveTotalBays} 
                  readOnly 
                  disabled
                  className="font-bold text-[13px] w-16 outline-none bg-transparent !border-0 cursor-default"
                />
              </div>

              {/* Contract Term */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className="w-3 h-3 !border !border-blue-600 flex-shrink-0 inline-block" />
                <span>
                  Contract term{' '}
                  <strong><input type="text" value={pd.contract_term?.value || ''}
                    onChange={e => patchProfile('contract_term', 'value', e.target.value)}
                    className="font-bold outline-none bg-transparent !border-0 w-12" /></strong>
                  ; start date{' '}
                  <strong><input type="text" value={pd.contract_period?.start_date || ''}
                    onChange={e => patchProfile('contract_period', 'start_date', e.target.value)}
                    className="font-bold outline-none bg-transparent !border-0 w-16" /></strong>
                  {' to '}
                  <strong><input type="text" value={pd.contract_period?.end_date || ''}
                    onChange={e => patchProfile('contract_period', 'end_date', e.target.value)}
                    className="font-bold outline-none bg-transparent !border-0 w-24" /></strong>
                </span>
              </div>

              {/* Operational Hours */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className="w-3 h-3 !border !border-blue-600 flex-shrink-0 inline-block" />
                <span>
                  Operational Hours{' '}
                  <input type="text" value={pd.operational_hours?.value || ''}
                    onChange={e => patchProfile('operational_hours', 'value', e.target.value)}
                    className="font-bold outline-none bg-transparent !border-0" />
                </span>
              </div>

              {/* VAT Note */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className="w-3 h-3 !border !border-blue-600 flex-shrink-0 inline-block" />
                <span>
                  Parking & Membership Charges are{' '}
                  <input type="text" value={pd.vat_note?.value || ''}
                    onChange={e => patchProfile('vat_note', 'value', e.target.value)}
                    className="outline-none bg-transparent !border-0" />
                </span>
              </div>

              {/* Assets Deployed */}
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                <span>Assets Deployed</span>
              </div>
            </div>
          </div>

          {/* ── Membership Table ── */}
          <div className="mb-4 ">
            <table className="w-170 !border-collapse text-[9px]">
              <thead>
                <tr>
                  {memColumns.map((col, i) => (
                    <th
                      key={i}
                      className={thCls}
                      style={{
                        width:
                          col.column_key === 'payment'                        ? '60px'  :
                          col.column_key === 'membership_period'              ? '90px'  :
                          col.column_key === 'excess_hours'                   ? '80px'  :
                          col.column_key === 'allowed_continuous_parking_hrs' ? '160px' :
                          undefined
                      }}
                    >
                      {col.label} 
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {memRows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {memColumns.map((col, cIdx) => (
                      <td key={cIdx} className={tdCls}>
                        <input type="text" value={row[col.column_key] || ''}
                          onChange={e => handleMembershipRow(rIdx, col.column_key, e.target.value)}
                          className="w-full outline-none bg-transparent !border-0 text-[9px]" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Bottom Row: Casual Parking + Parking Bays ── */}
          <div className="flex flex-row gap-3">

            {/* Casual Parking Price List */}
            <div className="overflow-hidden" style={{ minWidth: '220px' }}>
              <table className="w-full !border-collapse text-[9px]">
                <thead>
                  <tr>
                    <th className={thCls} style={{ minWidth: '160px' }}>{cd.columns?.[0]}</th>
                    <th className={thCls} style={{ minWidth: '60px' }}>{cd.columns?.[1]}</th>
                  </tr>
                </thead>
                <tbody>
                  {casualRows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      <td className={tdCls}>
                        <textarea
                          defaultValue={row[0] || ''} 
                          onChange={autoSave}
                          rows={1}
                          className="!border-0 bg-transparent outline-none text-[8px] text-left resize-none overflow-hidden"
                          style={{
                            minWidth: '260px',
                            minHeight: '22px',
                            lineHeight: '1.2',
                            padding: '3px 4px',
                          }}
                          onInput={e => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                        />
                      </td>
                      <td className={`${tdCls} text-center`}>
                        <input type="text" value={row[1] || ''}
                          onChange={e => handleCasualRow(rIdx, 1, e.target.value)}
                          className="w-full outline-none bg-transparent !border-0 text-center text-[9px]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 🔥 Parking Bays Breakdown - Total READ-ONLY, Individual bays EDITABLE */}
            <div className="overflow-hidden">
              <table className="w-full !border-collapse text-[9px]">
                <thead>
                  <tr className="bg-gray-300">
                    <th className="!border !border-black px-2 py-1 font-bold text-left text-[9px]">
                      {bd.columns?.[0]}
                    </th>
                    <th className="!border !border-black px-2 py-1 font-bold text-right text-[9px] whitespace-nowrap">
                      <div className="flex flex-col items-end">
                        <span>Parking Bays {effectiveTotalBays}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {baysRows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      <td className={tdCls}>
                        <input type="text" value={row[0] || ''}
                          onChange={e => handleBayRow(rIdx, 0, e.target.value)}
                          className="w-full outline-none bg-transparent !border-0 text-[9px]" />
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <input type="text" value={row[1] || ''}
                          onChange={e => handleBayRow(rIdx, 1, e.target.value)}
                          className="w-full outline-none bg-transparent !border-0 text-right text-[9px]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* ══════════════════ RIGHT COLUMN — Assets Deployed ══════════════════ */}
        <AssetsPanel
          assetsTable={{ title: assetsTable.title }}
          flatAssets={flatAssets}
          onSubCount={handleAssetSubCount}
          onTotalCount={handleAssetTotalCount}
        />

      </div>

    </div>
  );
};

export default DammamAirport_2;